import { ITableHeader } from "@dotlegal/dotlegal-ui-components";
import { Box, TableCell, TableRow } from "@mui/material";
import { Link } from "react-router";
import { useTranslation } from "@localization/useTranslation";
import { EntityType } from "@common/Common.types";
import EntityTypeIconWithName from "@common/components/entityTypeIcon/EntityTypeIconWithName";
import ReadOnlyTableDialog from "@common/components/readOnlyTableDialog/ReadOnlyTableDialog";
import { useUrlProvider } from "../../../../useUrlProvider";
import { FrameworkControlRiskAssessment } from "../FrameworkControlOverview.types";
import RiskLevelChip from "../../../../risk/RiskLevelChip";
import { getRiskLevelIndicatorChipStyle } from "../../../../riskSettings/riskArea/riskAreaDialog/riskLevels/riskLevelIndicators";

export interface ILinkedRiskAssessmentsDialog {
    riskAssessments: Array<FrameworkControlRiskAssessment>;
    onDialogClose: () => void;
}

export default function LinkedRiskAssessmentsDialog(props: ILinkedRiskAssessmentsDialog) {
    const { translateString } = useTranslation();
    const { getRiskAssessmentsOverviewUrl } = useUrlProvider();

    const headers: Array<ITableHeader<FrameworkControlRiskAssessment>> = [
        { property: "entityName", text: translateString("entity"), align: "left", showOnMobile: true },
        { property: "scenarioName", text: translateString("scenario"), align: "left", showOnMobile: true },
        { property: "riskAreaName", text: translateString("riskArea"), align: "left", showOnMobile: true },
        { property: "currentRiskLabel", text: translateString("currentRisk"), align: "left", showOnMobile: true },
    ];

    const renderCurrentRisk = (row: FrameworkControlRiskAssessment) => {
        if (row.currentRiskLevel == null) return null;
        const style = getRiskLevelIndicatorChipStyle(row.currentRiskLevel, row.currentRiskLevelIndicators);
        if (!style) return null;
        return <RiskLevelChip level={row.currentRiskLevel} label={row.currentRiskLabel} color={style.color} icon={style.icon} />;
    };

    return (
        <ReadOnlyTableDialog
            header={translateString("linkedRiskAssessments")}
            emptyText={translateString("noData")}
            headers={headers}
            data={props.riskAssessments}
            defaultOrderBy={"entityName"}
            clickableRows={false}
            hover={false}
            onDialogClose={props.onDialogClose}
            renderRow={(row) => (
                <TableRow key={row.riskAssessmentId}>
                    <TableCell>
                        <Box
                            component={Link}
                            sx={{ textDecoration: "none" }}
                            to={getRiskAssessmentsOverviewUrl(row.riskAreaId, row.entityType as EntityType, row.entityId)}
                        >
                            <Box
                                sx={(theme) => ({
                                    color: theme.palette.primary.main,
                                    fontWeight: theme.typography.fontWeightBold,
                                    "&:hover": { textDecoration: "underline" },
                                })}
                            >
                                <EntityTypeIconWithName type={row.entityType as EntityType} name={row.entityName} />
                            </Box>
                        </Box>
                    </TableCell>
                    <TableCell>{row.scenarioName}</TableCell>
                    <TableCell>{row.riskAreaName}</TableCell>
                    <TableCell>{renderCurrentRisk(row)}</TableCell>
                </TableRow>
            )}
        />
    );
}
